import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { getLanguageText, language as languageDictionary } from '../i18n/language';

const THEME_STORAGE_KEY = 'ontest_theme_preference';
const LANGUAGE_STORAGE_KEY = 'ontest_language';

const normalizeLanguage = (language) => {
    const value = (language || '').toLowerCase();

    if (value.startsWith('es')) {
        return 'es';
    }

    if (value.startsWith('en')) {
        return 'en';
    }

    return 'pt';
};

const getSystemTheme = () => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
        return 'light';
    }

    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

const getInitialThemePreference = () => {
    if (typeof window === 'undefined') {
        return 'system';
    }

    return localStorage.getItem(THEME_STORAGE_KEY) || 'system';
};

const getInitialLanguage = () => {
    if (typeof window === 'undefined') {
        return 'pt';
    }

    return localStorage.getItem(LANGUAGE_STORAGE_KEY) || normalizeLanguage(navigator.language);
};

export const PreferencesContext = createContext({
    theme: 'light',
    themePreference: 'system',
    language: 'pt',
    selectedLanguage: 'pt',
    languagePack: languageDictionary.pt,
    setThemePreference: () => {},
    setLanguage: () => {},
    t: (key) => key,
});

export const PreferencesProvider = ({ children }) => {
    const [systemTheme, setSystemTheme] = useState(getSystemTheme);
    const [themePreference, setThemePreferenceState] = useState(getInitialThemePreference);
    const [language, setLanguageState] = useState(getInitialLanguage);

    const theme = themePreference === 'system' ? systemTheme : themePreference;

    useEffect(() => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
            return undefined;
        }

        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        const onChange = (event) => setSystemTheme(event.matches ? 'dark' : 'light');

        if (typeof mediaQuery.addEventListener === 'function') {
            mediaQuery.addEventListener('change', onChange);
            return () => mediaQuery.removeEventListener('change', onChange);
        }

        mediaQuery.addListener(onChange);
        return () => mediaQuery.removeListener(onChange);
    }, []);

    useEffect(() => {
        document.documentElement.setAttribute('data-theme', theme);
        document.documentElement.style.colorScheme = theme;
    }, [theme]);

    useEffect(() => {
        document.documentElement.setAttribute('lang', language);
    }, [language]);

    const setThemePreference = (value) => {
        const nextValue = ['system', 'light', 'dark'].includes(value) ? value : 'system';
        localStorage.setItem(THEME_STORAGE_KEY, nextValue);
        setThemePreferenceState(nextValue);
    };

    const setLanguage = (value) => {
        const nextValue = normalizeLanguage(value);
        localStorage.setItem(LANGUAGE_STORAGE_KEY, nextValue);
        setLanguageState(nextValue);
    };

    const value = useMemo(() => ({
        theme,
        themePreference,
        language,
        selectedLanguage: language,
        languagePack: languageDictionary[language] || languageDictionary.pt,
        setThemePreference,
        setLanguage,
        t: (key) => getLanguageText(language, key),
    }), [language, theme, themePreference]);

    return (
        <PreferencesContext.Provider value={value}>
            {children}
        </PreferencesContext.Provider>
    );
};

export const usePreferences = () => useContext(PreferencesContext);
